import { App } from "vue";
import PrimeVue from "primevue/config";
import Aura from '@primeuix/themes/aura';
import { definePreset } from "@primeuix/themes";
import ToastService from 'primevue/toastservice';

/*
  The token bridge (visual-5). PrimeVue paints its own surfaces from Aura's neutral scale, and daisyUI paints the rest
  of the app from --color-base-*, so in dark mode a DataTable was a #171717 slab inside its #1b1b1b card, form fields
  were near-black (#0a0a0a) holes beside daisyUI inputs, a PrimeVue primary control was a lighter green with dark text,
  and the phone pinned column (index.css) was two-tone. These map the surfaces a member sees onto the daisyUI tokens,
  so both libraries follow one theme and a theme edit in index.css reaches PrimeVue too.
*/
const Base100 = 'var(--color-base-100)';
const PrimeVueBridge = {
    content: {
        background: Base100
    },
    formField: {
        background: Base100,
        // daisyUI's own .input border, so PrimeVue and daisyUI controls side by side draw the same outline.
        borderColor: 'color-mix(in oklab, var(--color-base-content) 20%, transparent)',
        hoverBorderColor: 'color-mix(in oklab, var(--color-base-content) 40%, transparent)'
    },
    overlay: {
        select: { background: Base100 },
        popover: { background: Base100 },
        modal: { background: Base100 }
    }
};

/*
  Opaque on purpose: the phone pinned first column (index.css) inherits its row's background, so a translucent row
  colour would let the scrolled columns read through it. Striping is a faint step of the text colour, so it lightens in
  dark and darkens in light; hover is the brand tint the Sectors table used to ask for (hover:bg-jade-400/25 in its pt,
  which PrimeVue's unlayered row background silently beat).
*/
const DataTableRowBridge = {
    stripedBackground: 'color-mix(in oklab, var(--color-base-content) 3%, var(--color-base-100))',
    hoverBackground: 'color-mix(in oklab, var(--color-primary) 12%, var(--color-base-100))'
};

export const CustomPreset = definePreset( Aura, {
    semantic: {
        primary: {
            50: '#EFFAF1',
            100: '#D9F2DB',
            200: '#B5E3BE',
            300: '#88CE9F',
            400: '#4CA973',
            500: '#329565',
            600: '#227755',
            700: '#1C5F4B',
            800: '#184E42',
            900: '#143D37',
            950: '#0B2322'
        },
        colorScheme: {
            light: {
                surface: {
                    0: '#FFFFFF',
                    50: '{neutral.50}',
                    100: '{neutral.100}',
                    200: '{neutral.200}',
                    300: '{neutral.300}',
                    400: '{neutral.400}',
                    500: '{neutral.500}',
                    600: '{neutral.600}',
                    700: '{neutral.700}',
                    800: '{neutral.800}',
                    900: '{neutral.900}',
                    950: '{neutral.950}'
                },
                primary: {
                    contrastColor: 'var(--color-primary-content)'
                },
                ...PrimeVueBridge
            },
            dark: {
                surface: {
                    0: '#FFFFFF',
                    50: '{neutral.50}',
                    100: '{neutral.100}',
                    200: '{neutral.200}',
                    300: '{neutral.300}',
                    400: '{neutral.400}',
                    500: '{neutral.500}',
                    600: '{neutral.600}',
                    700: '{neutral.700}',
                    800: '{neutral.800}',
                    900: '{neutral.900}',
                    950: '{neutral.950}'
                },
                // Aura's dark primary is the lighter {primary.400} with near-black text; daisyUI's btn-primary is
                // primary-500 with light text in both themes, and PrimeVue now matches it.
                primary: {
                    color: '{primary.500}',
                    contrastColor: 'var(--color-primary-content)',
                    hoverColor: '{primary.400}',
                    activeColor: '{primary.300}'
                },
                ...PrimeVueBridge
            }
        }
    },
    components: {
        datatable: {
            colorScheme: {
                light: { row: DataTableRowBridge },
                dark: { row: DataTableRowBridge }
            }
        },
        card: {
            colorScheme: {
                dark: {
                    root: {
                        background: '{surface.800}',
                        color: 'var(--color-base-content)'
                    }
                },
                light: {
                    root: {
                        color: 'var(--color-base-content)'
                    }
                }
            }
        }
    }
} );

const CustomThemeOptions = {
    theme: {
        preset: CustomPreset,
        options: {
            darkModeSelector: '[data-theme="dark"]',
        }
    },
    pt: {
        drawer: {
            // No width here: each Drawer sets its own. bg-base-100 needs `!` because PrimeVue's styles are
            // unlayered and outrank every Tailwind utility.
            root: {
                class: 'bg-base-100! h-full flex flex-col relative shadow-lg transition-transform duration-300 pointer-events-auto'
            }
        }
    },
    ptOptions: {
        mergeSections: true,
        mergeProps: false
    }
};

export default ( app: App ) => {
    app.use( PrimeVue, CustomThemeOptions );
    app.use( ToastService );
};
